import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CHECKOUT_EVENTS,
  CHECKOUT_MESSAGE_SOURCES,
  CHECKOUT_SESSION_STATES,
  CHECKOUT_TAB_STATES,
  CHECKOUT_TAB_POLL_MS,
  CHECKOUT_SIGNUP_ROUTE_PATH,
} from '../constants/bookingConst';
import {
  createCheckoutSessionId,
  buildCheckoutUrl,
  saveCheckoutSession,
  clearCheckoutSession,
  pruneStaleCheckoutSessions,
  openCheckoutChannel,
  openCheckoutTab,
  isTabOpen,
} from '../utils/checkoutSessionUtil';

/**
 * Opening-tab side of the checkout tab: opens it, hands it the order, listens
 * for what happens there, and notices when it is closed. Ported from the
 * court-booking system's hook of the same name.
 *
 * Handlers are read through a ref so the caller can pass fresh closures on
 * every render (they read current state) without re-subscribing.
 *
 * Used by the till's renewal (PosSubscribeModal) and the landing page's
 * sign-up (PosLandingSubscribeModal); `options.routePath` picks which
 * checkout page opens.
 *
 * @param {Object} handlers
 * @param {(payload: Object) => void} handlers.onSubmitted - submitted in the checkout tab; it is closing.
 * @param {() => void} handlers.onCancelled - the × in the checkout tab; it is closing.
 * @param {() => void} [handlers.onExpired] - the payment window ran out in the checkout tab.
 * @param {() => void} [handlers.onTabClosed] - the checkout tab was closed without finishing.
 * @param {Object} [options]
 * @param {string} [options.routePath] - checkout page route; defaults to the landing sign-up one.
 * @returns {{
 *   tabState: string,
 *   open: (record: Object) => void,
 *   reopen: () => void,
 *   end: () => void,
 *   isCheckoutTabOpen: () => boolean,
 * }}
 */
export const useCheckoutTab = (handlers, { routePath = CHECKOUT_SIGNUP_ROUTE_PATH } = {}) => {
  const [tabState, setTabState] = useState(CHECKOUT_TAB_STATES.IDLE);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const sessionIdRef = useRef(null);
  const sessionRef = useRef(null);
  const tabRef = useRef(null);
  const channelRef = useRef(null);

  /** Closes the message channel for the current session, if any. */
  const closeChannel = useCallback(() => {
    channelRef.current?.close();
    channelRef.current = null;
  }, []);

  /**
   * Forgets the current session: closes the channel and removes the stored
   * payload. Leaves the tab itself alone — callers decide about that.
   */
  const teardown = useCallback(() => {
    closeChannel();
    clearCheckoutSession(sessionIdRef.current);
    sessionIdRef.current = null;
    sessionRef.current = null;
  }, [closeChannel]);

  /** Closes the checkout tab if this tab opened it and it is still open. */
  const closeTab = useCallback(() => {
    if (isTabOpen(tabRef.current)) {
      try { tabRef.current.close(); } catch (error) { /* already gone */ }
    }
    tabRef.current = null;
  }, []);

  /**
   * Ends a checkout that finished in the checkout tab, and brings this tab
   * back to the front.
   * @param {Function|undefined} handler - the caller's handler to run afterwards.
   * @param {any} [arg]
   */
  const finishFromCheckout = useCallback((handler, arg) => {
    closeTab();
    teardown();
    setTabState(CHECKOUT_TAB_STATES.IDLE);
    try { window.focus(); } catch (error) { /* focus is best-effort */ }
    handler?.(arg);
  }, [closeTab, teardown]);

  /**
   * One message from the checkout tab.
   * @param {string} type - CHECKOUT_EVENTS value
   * @param {any} payload
   */
  const handleMessage = useCallback((type, payload) => {
    switch (type) {
      case CHECKOUT_EVENTS.REQUEST_SESSION:
        // The checkout tab booted after the order was handed over, or has no
        // localStorage — send it again.
        if (sessionRef.current) channelRef.current?.post(CHECKOUT_EVENTS.SESSION, sessionRef.current);
        break;
      case CHECKOUT_EVENTS.SUBMITTED:
        finishFromCheckout(handlersRef.current.onSubmitted, payload);
        break;
      case CHECKOUT_EVENTS.CANCELLED:
        finishFromCheckout(handlersRef.current.onCancelled);
        break;
      case CHECKOUT_EVENTS.EXPIRED:
        finishFromCheckout(handlersRef.current.onExpired);
        break;
      default:
        break;
    }
  }, [finishFromCheckout]);

  /**
   * Opens the checkout tab with the order in it. Call synchronously from the
   * click: browsers only allow window.open during a user gesture.
   *
   * Unlike the booking system, nothing has to be written before checkout —
   * the order is complete when the button is pressed — so the tab is opened
   * and handed the order in one step rather than prepared and launched.
   *
   * @param {Object} record - everything the checkout page needs to render and submit.
   */
  const open = useCallback((record) => {
    closeTab();
    teardown();
    pruneStaleCheckoutSessions();

    const sessionId = createCheckoutSessionId();
    const session = { state: CHECKOUT_SESSION_STATES.READY, record };
    sessionIdRef.current = sessionId;
    sessionRef.current = session;
    // Stored before the tab opens, so the tab finds it on its first read.
    saveCheckoutSession(sessionId, session);
    channelRef.current = openCheckoutChannel(sessionId, CHECKOUT_MESSAGE_SOURCES.OPENER, handleMessage);

    tabRef.current = openCheckoutTab(buildCheckoutUrl(sessionId, routePath));
    setTabState(tabRef.current ? CHECKOUT_TAB_STATES.OPEN : CHECKOUT_TAB_STATES.BLOCKED);
  }, [closeTab, teardown, handleMessage, routePath]);

  /**
   * Brings the checkout tab back — focuses it if open, otherwise opens it
   * again on the same session. Call from a click.
   */
  const reopen = useCallback(() => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;
    if (isTabOpen(tabRef.current)) {
      try { tabRef.current.focus(); } catch (error) { /* focus is best-effort */ }
      return;
    }
    // Re-store the payload: a reopened tab reads it on boot.
    if (sessionRef.current) saveCheckoutSession(sessionId, sessionRef.current);
    tabRef.current = openCheckoutTab(buildCheckoutUrl(sessionId, routePath));
    setTabState(tabRef.current ? CHECKOUT_TAB_STATES.OPEN : CHECKOUT_TAB_STATES.BLOCKED);
  }, [routePath]);

  /**
   * Ends the checkout from this tab (backed out, or the dialog closed): tells
   * the checkout tab to close, closes it, forgets the session.
   */
  const end = useCallback(() => {
    channelRef.current?.post(CHECKOUT_EVENTS.CLOSE);
    closeTab();
    teardown();
    setTabState(CHECKOUT_TAB_STATES.IDLE);
  }, [closeTab, teardown]);

  /** @returns {boolean} whether the checkout tab is currently open. */
  const isCheckoutTabOpen = useCallback(() => isTabOpen(tabRef.current), []);

  // Notice the checkout tab being closed. There is no event for another tab
  // closing, so the handle is polled while one is expected to be open.
  useEffect(() => {
    if (tabState !== CHECKOUT_TAB_STATES.OPEN) return undefined;
    const interval = setInterval(() => {
      if (!tabRef.current || isTabOpen(tabRef.current)) return;
      setTabState(CHECKOUT_TAB_STATES.CLOSED);
      handlersRef.current.onTabClosed?.();
    }, CHECKOUT_TAB_POLL_MS);
    return () => clearInterval(interval);
  }, [tabState]);

  // Leaving the screen entirely: drop the channel. The stored payload stays,
  // so an open checkout tab can still finish on its own.
  useEffect(() => closeChannel, [closeChannel]);

  return { tabState, open, reopen, end, isCheckoutTabOpen };
};
